import { yupResolver } from '@hookform/resolvers/yup'
import { find, findIndex, map } from 'lodash-es'
import { ChangeEvent, useEffect, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import {
  FooterBanner,
  FooterSettingsPayload,
  Languages,
} from '../../../../common/models'
import FormGroup from '../../../../core/components/FormGroup'
import { FormUploadPreview } from '../../../../core/components/FormUploadPreview'
import {
  deleteSettingsFooterBanner,
  getFooterSettings,
  updateFooterSettings,
} from '../../../../core/services/festival-settings'
import { uploadImage } from '../../../../core/services/upload-image'
import { DeleteConfirmationModal } from '../../DeleteConfirmationModal'
import { MessageBox } from '../../messages/MessageBox'
import { Button, Card, Input } from '@/components/ui'
import { Info } from 'lucide-react'
import { Icon } from '@/common/components/Icon'

interface IFooterManagementValues {
  copyrightTexts: {
    id?: string
    locale: string
    text: string
  }[]
  banners: {
    id?: string
    bannerLink: string
    bannerImageKey: string
  }[]
}

interface FooterManagementProps {
  allLanguages: Languages[]
  hasEditPermission: boolean
}

export const FooterManagement: React.FC<FooterManagementProps> = (
  props: FooterManagementProps,
) => {
  const { allLanguages, hasEditPermission } = props
  const { editionId, festivalName } = useParams()
  const [invalidPhoto, setInvalidPhoto] = useState<boolean[]>([])
  const [showDeleteConfirmationModalLogo, setDeleteConfirmationModalLogo] =
    useState<boolean>(false)
  const [footerBanner, setFooterBanner] = useState<FooterBanner[]>([])
  const [footerImagesNames, setFooterImagesNames] = useState<string[]>([])
  const [removeLogoState, setRemoveLogoState] = useState<number>(0)
  const [isUploading, setIsUploading] = useState<boolean>(false)

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [isErrorRatio, setIsErrorRatio] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const { data } = await getFooterSettings()
        const langTemp = map(allLanguages, (lang) => {
          return {
            locale: lang.locale,
            text: '',
          }
        })
        setTimeout(() => {
          setValue('copyrightTexts', langTemp)
        }, 200)
        setFooterBanner(data.banners)
        const temp = map(data.banners, (d) => {
          const temp = invalidPhoto
          temp.push(false)
          setInvalidPhoto(temp)
          return {
            id: d.id,
            bannerLink: d.bannerLink,
            bannerImageKey: d.bannerImageLink,
          }
        })
        setTimeout(() => {
          setValue('banners', temp)
        }, 200)
        setTimeout(() => {
          map(data.copyrightTexts, (trans) => {
            const index = findIndex(
              allLanguages,
              (l) => l.locale === trans.locale,
            )
            if (index !== -1) {
              if (data.copyrightTexts) {
                setValue(`copyrightTexts.${index}`, data.copyrightTexts[index])
              } else {
                resetField(`copyrightTexts.${index}`)
              }
            }
          })
        }, 100)
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName, allLanguages])

  const schema = yup.object().shape({
    copyrightTexts: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.string(),
          locale: yup.string().required('Field is required'),
          text: yup.string().required('Field is required'),
        }),
      )
      .required(),
    banners: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.string(),
          bannerLink: yup.string().required('Field is required'),
          bannerImageKey: yup.string().required('Field is required'),
        }),
      )
      .required(),
  })

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    resetField,
    control,
  } = useForm<IFooterManagementValues>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<IFooterManagementValues>(schema),
    defaultValues: {},
  })

  const submit = async (values: IFooterManagementValues) => {
    try {
      const temp: FooterSettingsPayload = values
      temp.banners = map(temp.banners, (banner, index) => {
        return {
          id: banner.id,
          bannerLink: banner.bannerLink,
          bannerImageKey:
            banner.bannerImageKey !== footerBanner[index].bannerImageLink
              ? banner.bannerImageKey
              : null,
        }
      })
      const { status } = await updateFooterSettings(temp)
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const {
    fields: footerLogoFields,
    append: appendFooterLogo,
    remove: removeFooterLogo,
  } = useFieldArray({
    control: control,
    name: 'banners',
  })

  const watchFooterLogoFields = watch('banners')

  const { fields: footerLanguagesFields } = useFieldArray({
    control: control,
    name: 'copyrightTexts',
  })

  const deleteLogoFunc = () => {
    // prettier-ignore
    (async () => {
      try {
        removeFooterLogo(removeLogoState)
        if (watchFooterLogoFields?.[removeLogoState]?.id) {
          const { status } = await deleteSettingsFooterBanner(
            watchFooterLogoFields[removeLogoState]?.id || '',
          )
          const responseFooter2 = await getFooterSettings()
          const banners = map(responseFooter2.data.banners, (data) => {
            return {
              id: data.id,
              bannerLink: data.bannerLink,
              bannerImageKey: 'x',
            }
          })
          setTimeout(() => {
            setValue('banners', banners)
          }, 0)
          if (status === 204 && responseFooter2.status === 200) {
            setIsError(false)
            setShowMessageBox(true)
            setMessage('Delete Successful')
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }

  const handleCancel = async () => {
    setTimeout(() => {
      map(footerBanner, (d, index) => {
        setValue(`banners.${index}.id`, d.id)
        setValue(`banners.${index}.bannerLink`, d.bannerLink)
        setValue(`banners.${index}.bannerImageKey`, d.bannerImageLink)
      })
    }, 200)
  }

  return (
    <>
      <Card className="p-7.5 bg-gradient-4 border border-monochrome-120">
        <form
          id="settings-footer-management-form"
          onSubmit={handleSubmit(submit)}
        >
          <h3 className="text-title-xs text-monochrome-1 mb-4">
            Footer Management
          </h3>
          <div className="flex flex-col items-start gap-3 w-full">
            {map(footerLanguagesFields, (field, index) => (
              <Card
                key={index}
                className="grid grid-cols-2 p-3 bg-monochrome-3-60 border border-monochrome-120 rounded-2xl w-full gap-3"
              >
                <FormGroup label="Footer Copyright">
                  <Input
                    isInvalid={!!errors?.copyrightTexts?.[index]?.text?.message}
                    {...register(`copyrightTexts.${index}.text`)}
                    type="text"
                    placeholder="Enter Footer Copyright"
                    disabled={!hasEditPermission}
                  />
                </FormGroup>
                <FormGroup label="Language">
                  <Input
                    value={
                      find(allLanguages, (cont) => field.locale === cont.locale)
                        ?.name
                    }
                    readOnly
                    className="bg-monochrome-120"
                  />
                </FormGroup>
              </Card>
            ))}
            {map(footerLogoFields, (field, index) => {
              let imageName = undefined
              if (footerBanner[index]?.bannerImageLink) {
                const url = new URL(footerBanner[index]?.bannerImageLink)
                const i = url?.pathname.lastIndexOf('/') + 1
                imageName = url?.pathname?.substring(i)
              }
              return (
                <Card
                  key={field.id}
                  className="flex flex-col p-3 bg-monochrome-3-60 border border-monochrome-120 rounded-2xl w-full gap-3"
                >
                  <FormUploadPreview
                    formGroupProps={{
                      labelStyle: { minWidth: '200px', maxWidth: '200px' },
                    }}
                    control={
                      <Input
                        multiple={false}
                        accept="image/*"
                        isInvalid={
                          !!errors?.banners?.[index]?.bannerLink?.message ||
                          invalidPhoto[index] ||
                          isErrorRatio
                        }
                        disabled={!hasEditPermission}
                        name="photo"
                        onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                          const file = e?.target?.files?.[0]
                          if (file) {
                            if (file.size > 1_000_000) {
                              const temp = invalidPhoto
                              temp[index] = true
                              setInvalidPhoto(temp)
                              setIsError(true)
                              setShowMessageBox(true)
                              setMessage('The photo is too large (1 MB)')
                              return
                            }
                            const { photoKey, photoUrl } = await uploadImage(
                              file,
                              setIsUploading,
                              setIsError,
                              setShowMessageBox,
                              setMessage,
                              setIsErrorRatio,
                              'SETTINGS_FOOTER_BANNER',
                              false,
                            )
                            if (photoUrl && photoKey) {
                              setValue(
                                `banners.${index}.bannerImageKey`,
                                photoKey,
                                {
                                  shouldValidate: true,
                                },
                              )
                              const items = [...footerBanner]
                              const item = { ...items[index] }
                              item.bannerImageLink = photoUrl
                              items[index] = item
                              setFooterBanner(items)
                              const items2 = [...footerImagesNames]
                              items2[index] = file.name
                              setFooterImagesNames(items2)
                            } else {
                              const temp = invalidPhoto
                              temp[index] = true
                              setInvalidPhoto(temp)
                            }
                          }
                        }}
                        type="file"
                      />
                    }
                    imageName={imageName}
                    isUploading={isUploading}
                    url={footerBanner?.[index]?.bannerImageLink}
                    onRemove={() => {
                      setValue(`banners.${index}.bannerImageKey`, '')
                      const items = [...footerBanner]
                      items[index].bannerImageLink = ''
                      setFooterBanner(items)
                      const names = [...footerImagesNames]
                      names[index] = ''
                      setFooterImagesNames(names)
                    }}
                    imageStyle={{
                      objectFit: 'contain',
                    }}
                    label="Footer Banner (120X30)"
                    previewLabel="Footer Banner (120X30)"
                  />
                  {isErrorRatio && invalidPhoto[index] && (
                    <Card className="p-3 flex flex-row items-center gap-2">
                      <Info className="w-4 h-4 text-accent-3" />
                      <span className="text-sm text-monochrome-120">
                        The image uploaded has a different aspect ration than
                        required. Please try again.
                      </span>
                    </Card>
                  )}
                  <FormGroup label="Footer Banner Link">
                    <Input
                      isInvalid={
                        !!errors?.banners?.[index]?.bannerLink?.message
                      }
                      {...register(`banners.${index}.bannerLink`)}
                      type="text"
                      placeholder="Enter Footer Logo Link"
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                  {footerLogoFields.length > 0 && (
                    <div className="flex justify-end">
                      <Button
                        onClick={() => {
                          setDeleteConfirmationModalLogo(true)
                          setRemoveLogoState(index)
                        }}
                        variant="destructive"
                        disabled={!hasEditPermission}
                      >
                        Delete
                        <Icon
                          icon="close-circle"
                          width="18"
                          height="18"
                          viewBox="0 0 18 18"
                          fill="none"
                          className="shrink-0"
                        />
                      </Button>
                    </div>
                  )}
                </Card>
              )
            })}
            <div className="flex justify-center items-center w-full">
              <Button
                onClick={() =>
                  appendFooterLogo({
                    id: '',
                    bannerLink: '',
                    bannerImageKey: '',
                  })
                }
                type="button"
                variant="secondary"
                disabled={!hasEditPermission}
              >
                Add new Logo
                <Icon
                  icon="plus"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  className="shrink-0"
                />
              </Button>
            </div>
          </div>
        </form>

        <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-monochrome-120 [&>*]:w-[154px]">
          <Button
            onClick={handleCancel}
            variant="secondary"
            data-dismiss="modal"
            disabled={!hasEditPermission}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            form="settings-footer-management-form"
            type="submit"
            disabled={isSubmitting || isUploading || !hasEditPermission}
            loading={isSubmitting}
          >
            Save
          </Button>
        </div>
      </Card>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModalLogo}
        setDeleteConfirmationModal={setDeleteConfirmationModalLogo}
        deleteTitle={'Banner'}
        deleteFunc={deleteLogoFunc}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
